import { useLayoutEffect, useRef, useState, type ReactElement } from "react";
import type { BagRefusalReason, Rules } from "odyssey-shared";
import { ConfirmDialog, Menu, type MenuEntry } from "../../ui/kit/index.ts";
import { cssPerLogical } from "../../ui/kit/measure.ts";
import { destroyBody, dropTitle, itemName, DESTROY_WIDTH, DROP_WIDTH } from "./describe.ts";
import type { BagSlot } from "./store.ts";
import { say } from "../../words.ts";

/**
 * The Inventory window's item menu and its Destroy confirm (ADR-091's note for P2.8-T14 of the author's controls of
 * 2026-10-08; ADR-125 decision 7). The menu is the kit's `Menu` drawn as the design's item menu (`ItemSurfaces.jsx`,
 * `item-menu`): 128 wide, wider only as its heading or an entry needs, as the design's menu takes its width as its
 * least (so "Minor Healing Potion (5)" is never cut short), a heading naming the item with a stack's count in brackets, then Consume for a consumable,
 * first; Split stack; Keep in place, or Let it move for a stack already kept (P2.8-T49, ADR-125 decision 6.5); and,
 * after a divider, Drop (P2.8-T34), with no confirm, since a dropped item can be picked up again, and Destroy, each
 * leading with its glyph. An entry the item cannot take
 * now stays, disabled, with its reason at its right: Split stack "Not a stack" for a stack of 1 and "No empty slot" for
 * a full bag, Drop and Destroy "Quest item" for a quest item, which the server refuses too, and Destroy then not in the
 * danger colour. An entry whose system does not exist (Equip, Read, Send to chat) is left out. It opens at the
 * pointer, or at the slot for the keyboard, and is kept 4 logical pixels inside the screen. It holds the keyboard while
 * open and Escape closes it (the kit's). Destroy asks first with the kit's `ConfirmDialog`, whose body names the item
 * and its count.
 */

/**
 * What the menu's entries do.
 */
export type BagMenuChoice = "consume" | "place" | "belt" | "split" | "keep" | "drop" | "destroy";

/**
 * The menu's least width in logical pixels, the design's item menu's.
 */
export const BAG_MENU_WIDTH = 128;

/**
 * How far inside the screen's edge the menu is kept, in logical pixels (the design's "kept 4 inside the screen edge").
 */
export const BAG_MENU_EDGE = 4;

/**
 * Where a menu opens: the slot it is for and the point, in client CSS pixels, of its top left corner before it is kept
 * inside the screen.
 */
export interface BagMenuAt {
  readonly slot: number;
  readonly x: number;
  readonly y: number;
}

/**
 * What the menu reads of the slot it opens on: its item, why the bag's rule would refuse its split, null when it
 * would not, whether a split would be sent now (no answer waiting on the slot, a sender bound), and whether the stack
 * is kept in place, absent for false, and whether the item can be placed on the bar now (P2.8-T29), absent for false, and whether it is a tool the tool belt
 * can take now (ADR-142 decision 3b), absent for false.
 */
export interface BagMenuSlot {
  readonly item: string;
  readonly splitRefusal: BagRefusalReason | null;
  readonly canSplit: boolean;
  readonly kept?: boolean;
  readonly placeable?: boolean;
  readonly beltable?: boolean;
}

/**
 * The menu's entries for a slot: Consume first for a consumable, then Place on bar for an item the bar can take now
 * (P2.8-T29, ADR-121 decision 3.3: it starts the bar's placing, as Ctrl+Enter does), Put on belt for a tool the tool belt can
 * take now (ADR-142 decision 3b: the first empty slot), Split stack, Keep in place (Let it move for a kept
 * stack, its `lock` glyph either way), then, after a divider, Drop (P2.8-T34, ADR-091's amendment for P2.8-T34, point
 * 8) and Destroy, as the design's item menu draws them. Split stack is disabled when it would not be sent, saying why
 * when the bag's rule refuses it; Drop and Destroy are disabled for a quest item, saying so, and Destroy is in the
 * danger colour otherwise.
 *
 * @param rules The rules the item is read from.
 * @param slot The slot's item and its split's state.
 * @returns The entries.
 * @example
 * bagMenuEntries(PAGE_RULES, { item: "minor_healing_potion", splitRefusal: "empty", canSplit: false }).map((entry) => entry.why ?? "");
 * // ["", "Not a stack", "", ""]
 */
export function bagMenuEntries(rules: Rules, slot: BagMenuSlot): MenuEntry<BagMenuChoice>[] {
  const kind = rules.items.get(slot.item)?.kind;
  const entries: MenuEntry<BagMenuChoice>[] = [];
  if (kind === "consumable") {
    entries.push({ value: "consume", label: say("bag.menu.consume"), glyph: "drop-small" });
  }
  if (slot.placeable === true) {
    entries.push({ value: "place", label: say("bag.menu.place"), glyph: "arrow-right-small" });
  }
  if (slot.beltable === true) {
    entries.push({ value: "belt", label: say("bag.menu.belt"), glyph: "arrow-right-small" });
  }
  const splitWhy = slot.splitRefusal === "empty" ? say("bag.menu.notStack") : slot.splitRefusal === "full" ? say("bag.menu.noRoom") : undefined;
  entries.push({ value: "split", label: say("bag.menu.split"), glyph: "grid", disabled: !slot.canSplit, ...(splitWhy === undefined ? {} : { why: splitWhy }) });
  entries.push({ value: "keep", label: slot.kept === true ? say("bag.menu.letMove") : say("bag.menu.keep"), glyph: "lock" });
  const quest = kind === "quest";
  entries.push({ value: "drop", label: say("bag.menu.drop"), glyph: "arrow-right-small", divided: true, disabled: quest, ...(quest ? { why: say("bag.menu.quest") } : {}) });
  entries.push({ value: "destroy", label: say("bag.menu.destroy"), glyph: "close-small", danger: !quest, disabled: quest, ...(quest ? { why: say("bag.menu.quest") } : {}) });
  return entries;
}

/**
 * The open menu, fixed at its point and moved back inside the screen once it has a size.
 *
 * @param props Where it opens (`at`), its `heading` (the item's name), its `entries`, `onSelect` with the entry chosen,
 *   and `onClose`, which Escape, Tab and a choice call.
 * @returns The menu.
 * @throws Error when mounted outside a `KitInputProvider`.
 */
export function BagMenu({ at, heading, entries, onSelect, onClose }: { readonly at: BagMenuAt; readonly heading: string; readonly entries: readonly MenuEntry<BagMenuChoice>[]; readonly onSelect: (choice: BagMenuChoice) => void; readonly onClose: () => void }): ReactElement {
  const host = useRef<HTMLDivElement>(null);
  const [place, setPlace] = useState({ x: at.x, y: at.y });

  useLayoutEffect(() => {
    const box = host.current;
    if (box === null) {
      return;
    }
    const width = box.offsetWidth;
    const height = box.offsetHeight;
    const edge = (cssPerLogical(box) ?? 1) * BAG_MENU_EDGE;
    const x = Math.max(edge, Math.min(at.x, window.innerWidth - edge - width));
    const y = Math.max(edge, Math.min(at.y, window.innerHeight - edge - height));
    setPlace((before) => (before.x === x && before.y === y ? before : { x, y }));
  }, [at.x, at.y]);

  return (
    <div ref={host} className="ot-bag-menu" data-bag="menu" data-slot={at.slot} style={{ left: `${place.x}px`, top: `${place.y}px` }}>
      <Menu label={heading} heading={heading} minWidth={BAG_MENU_WIDTH} items={entries} onSelect={onSelect} onClose={onClose} />
    </div>
  );
}

/**
 * The Drop confirm for an item of Rare and above (the author's ruling of 2026-10-10 for P2.8-T34): the kit's confirm
 * dialog, its title naming the item and a stack's count, its body that anyone nearby can pick it up, its confirm button
 * "Drop", focused first as every confirm's is; Escape and Cancel leave the stack in the bag.
 *
 * @param props The `rules` the item is named by, the `slot` as it was when the drop was asked, `onConfirm` and
 *   `onCancel`.
 * @returns The dialog.
 * @throws Error when mounted outside a `KitInputProvider`.
 */
export function DropConfirm({ rules, slot, onConfirm, onCancel }: { readonly rules: Rules; readonly slot: BagSlot & { readonly item: string }; readonly onConfirm: () => void; readonly onCancel: () => void }): ReactElement {
  return (
    <ConfirmDialog title={dropTitle(itemName(rules, slot.item), slot.quantity)} width={DROP_WIDTH} confirmLabel={say("bag.drop.confirm")} cancelLabel={say("bag.drop.cancel")} onConfirm={onConfirm} onCancel={onCancel}>
      {say("bag.drop.body")}
    </ConfirmDialog>
  );
}

/**
 * The Destroy confirm: the kit's confirm dialog, its title asking, its body naming the count and the item and that it
 * cannot be undone, its confirm button "Destroy" in the danger variant, focused first as every confirm's is.
 *
 * @param props The `rules` the item is named by, the `slot` as it was when Destroy was chosen, `onConfirm` and
 *   `onCancel`.
 * @returns The dialog.
 * @throws Error when mounted outside a `KitInputProvider`.
 */
export function DestroyConfirm({ rules, slot, onConfirm, onCancel }: { readonly rules: Rules; readonly slot: BagSlot & { readonly item: string }; readonly onConfirm: () => void; readonly onCancel: () => void }): ReactElement {
  const name = itemName(rules, slot.item);
  return (
    <ConfirmDialog title={say("bag.destroy.title")} width={DESTROY_WIDTH} confirmLabel={say("bag.destroy.confirm")} cancelLabel={say("bag.destroy.cancel")} onConfirm={onConfirm} onCancel={onCancel}>
      {destroyBody(name, slot.quantity)}
    </ConfirmDialog>
  );
}
